import { useCallback, useEffect, useRef, useState } from "react"
import { getUnreadThreadCount } from "src/apis/contact/getUnreadThreadCount"
import { Buckets } from "src/aws/customAPI"
import { ContactOverviewModel } from "src/types/contact/ContactOverviewModel"

type UnreadCountMap = Map<string, number>

interface UseIncrementalUnreadCountParams {
  contacts?: {
    contacts: ContactOverviewModel[]
    nextToken?: string
    total?: number | null
  }
  request: {
    accessToken: string
    accountId: string
  } | null
}

/**
 * 増分的に未読数を取得・管理するフック
 *
 * ページング時に新しい連絡のみの未読数を取得し、
 * 既存のデータとマージすることでOpenSearchの制限を回避します
 */
export const useIncrementalUnreadCount = ({ contacts, request }: UseIncrementalUnreadCountParams) => {
  // 未読カウントをMapで管理（高速な検索のため）
  const [unreadCountMap, setUnreadCountMap] = useState<UnreadCountMap>(new Map())

  // Buckets形式のデータ（既存コンポーネントとの互換性のため）
  const [unreadPerContact, setUnreadPerContact] = useState<Buckets[]>([])

  // 前回処理した連絡IDを保持
  const processedContactIds = useRef<Set<string>>(new Set())

  // 連絡リストがリセットされた場合の処理
  const resetUnreadCounts = useCallback(() => {
    processedContactIds.current = new Set()
    setUnreadCountMap(new Map())
    setUnreadPerContact([])
  }, [])

  useEffect(() => {
    if (!request || !contacts?.contacts || contacts.contacts.length === 0) {
      return
    }

    // 新しい連絡IDを抽出
    const newContactIds = contacts.contacts
      .map(contact => contact.id)
      .filter(id => !processedContactIds.current.has(id))

    if (newContactIds.length === 0) {
      return
    }


    // 新しい連絡の未読数を取得
    getUnreadThreadCount({
      ...request,
      contactIds: newContactIds
    }).then(data => {
      if (!data?.buckets) return

      // 新しいデータをMapにマージ
      setUnreadCountMap(prevMap => {
        const newMap = new Map(prevMap)
        data.buckets.forEach((bucket: Buckets) => {
          newMap.set(bucket.key, bucket.doc_count)
        })
        return newMap
      })

      // 処理済みIDを更新
      newContactIds.forEach(id => {
        processedContactIds.current.add(id)
      })

      // Buckets形式のデータも更新（既存コンポーネントとの互換性のため）
      setUnreadPerContact(prevBuckets => {
        const existingKeys = new Set(prevBuckets.map(b => b.key))
        const newBuckets = data.buckets.filter((b: Buckets) => !existingKeys.has(b.key))
        return [...prevBuckets, ...newBuckets]
      })

    }).catch(error => {
      console.error('[useIncrementalUnreadCount] 未読数の取得に失敗しました:', error)
    })
  }, [request, contacts])

  // 連絡リストがクリアされた場合（例：フィルタ変更、検索）
  useEffect(() => {
    if (!contacts?.contacts || contacts.contacts.length === 0) {
      resetUnreadCounts()
    }
  }, [contacts?.contacts, resetUnreadCounts])

  return {
    unreadPerContact,
    unreadCountMap,
    resetUnreadCounts,
    getUnreadCount: useCallback((contactId: string) => {
      return unreadCountMap.get(contactId) || 0
    }, [unreadCountMap])
  }
}